import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/auth/AuthContext';
import {
  ConversationSummary,
  Message,
  getAllConversations,
  getMessages,
  sendMessage,
  closeConversation,
  reopenConversation
} from '../../lib/services/messageService';

const Messages: React.FC = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ConversationSummary | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed'>('open');
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const processedMessageIds = useRef<Set<string>>(new Set());

  // Check if conversation needs attention (last message from customer)
  const needsAttention = (conv: ConversationSummary) => {
    if (conv.status === 'closed') return false;
    if (!conv.last_message) return true; // New conversation
    // Use last_message_sender_type if available
    if (conv.last_message_sender_type) {
      return conv.last_message_sender_type === 'customer';
    }
    return true; // Fallback
  };

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    const data = await getAllConversations(statusFilter);
    setConversations(data);

    // Calculate unread count (conversations needing attention)
    const openConversations = statusFilter === 'open' ? data : await getAllConversations('open');
    const needsReplyCount = openConversations.filter(conv => {
      if (!conv.last_message) return true;
      if (conv.last_message_sender_type) {
        return conv.last_message_sender_type === 'customer';
      }
      return true;
    }).length;
    setUnreadCount(needsReplyCount);

    setIsLoading(false);
  }, [statusFilter]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Subscribe to new conversations and messages (to update list when staff replies)
  useEffect(() => {
    const channel = supabase
      .channel('staff-conversations-and-messages')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations'
        },
        () => {
          fetchConversations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        () => {
          // Refresh conversation list when any message is sent
          fetchConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchConversations]);

  // Fetch messages when conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      const loadMessages = async () => {
        const msgs = await getMessages(selectedConversation.id);
        setMessages(msgs);
        msgs.forEach(m => processedMessageIds.current.add(m.id));
      };
      loadMessages();
    } else {
      setMessages([]);
      processedMessageIds.current.clear();
    }
  }, [selectedConversation]);

  // Subscribe to new messages for selected conversation
  useEffect(() => {
    if (!selectedConversation) return;

    const channel = supabase
      .channel(`staff-messages-${selectedConversation.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${selectedConversation.id}`
        },
        (payload) => {
          const messageId = payload.new.id;
          if (processedMessageIds.current.has(messageId)) return;

          processedMessageIds.current.add(messageId);
          const newMessage: Message = {
            id: payload.new.id,
            conversation_id: payload.new.conversation_id,
            sender_type: payload.new.sender_type,
            sender_id: payload.new.sender_id,
            content: payload.new.content,
            created_at: payload.new.created_at
          };
          setMessages(prev => [...prev, newMessage]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedConversation]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || !selectedConversation || isSending) return;

    setIsSending(true);
    const newMessage = await sendMessage(
      selectedConversation.id,
      inputValue.trim(),
      'staff',
      user?.id
    );

    if (newMessage && !processedMessageIds.current.has(newMessage.id)) {
      processedMessageIds.current.add(newMessage.id);
      setMessages(prev => [...prev, newMessage]);
    }

    setInputValue('');
    setIsSending(false);
  };

  // Close/reopen conversation
  const handleToggleStatus = async () => {
    if (!selectedConversation) return;

    if (selectedConversation.status === 'open') {
      await closeConversation(selectedConversation.id);
      setSelectedConversation({ ...selectedConversation, status: 'closed' });
    } else {
      await reopenConversation(selectedConversation.id);
      setSelectedConversation({ ...selectedConversation, status: 'open' });
    }
    fetchConversations();
  };

  // Format timestamp
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return format(date, 'h:mm a');
    }
    return format(date, 'MMM d, h:mm a');
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-[#344854]">Messages</h1>
          {unreadCount > 0 && (
            <span className="inline-flex items-center justify-center px-2.5 py-1 text-sm font-bold text-white bg-amber-500 rounded-full animate-pulse">
              {unreadCount} needs reply
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'open' | 'closed')}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
          >
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
        {/* Conversation list */}
        <div className="w-80 border-r border-gray-200 flex flex-col">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-sm text-[#344854]">
              Conversations ({conversations.length})
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : conversations.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No {statusFilter === 'all' ? '' : statusFilter} conversations
              </div>
            ) : (
              conversations.map((conv) => {
                const isUnread = needsAttention(conv);
                const isSelected = selectedConversation?.id === conv.id;

                return (
                  <button
                    key={conv.id}
                    onClick={() => setSelectedConversation(conv)}
                    className={`w-full p-3 text-left border-b border-gray-100 transition-colors ${
                      isSelected
                        ? 'bg-[#E8F5F2]'
                        : isUnread
                        ? 'bg-amber-50 hover:bg-amber-100'
                        : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {isUnread && (
                            <span className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0" />
                          )}
                          <span className={`text-sm truncate ${
                            isUnread ? 'font-semibold text-[#344854]' : 'font-medium text-gray-600'
                          }`}>
                            {conv.customer_name || conv.guest_email || 'Unknown'}
                          </span>
                          {conv.status === 'closed' && (
                            <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
                              Closed
                            </span>
                          )}
                        </div>
                        <p className={`text-xs truncate mt-0.5 ${
                          isUnread ? 'text-gray-700 font-medium' : 'text-gray-500'
                        }`}>
                          {conv.last_message || 'No messages yet'}
                        </p>
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {conv.last_message_at ? formatTime(conv.last_message_at) : ''}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col">
          {selectedConversation ? (
            <>
              {/* Chat header */}
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-[#344854]">
                    {selectedConversation.customer_name || selectedConversation.guest_email || 'Unknown'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {selectedConversation.user_email || selectedConversation.guest_email || ''}
                    {selectedConversation.message_count > 0 && ` • ${selectedConversation.message_count} messages`}
                  </p>
                </div>
                <button
                  onClick={handleToggleStatus}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    selectedConversation.status === 'open'
                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      : 'bg-[#3A5E63] text-white hover:bg-[#2d4a4f]'
                  }`}
                >
                  {selectedConversation.status === 'open' ? 'Close Conversation' : 'Reopen'}
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-500 text-sm">
                    No messages yet
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex mb-3 ${
                        message.sender_type === 'staff' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                          message.sender_type === 'staff'
                            ? 'bg-[#3A5E63] text-white rounded-br-md'
                            : 'bg-white text-[#344854] rounded-bl-md border border-gray-200'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">
                          {message.content}
                        </p>
                        <div
                          className={`text-xs mt-1 ${
                            message.sender_type === 'staff'
                              ? 'text-white/70'
                              : 'text-gray-400'
                          }`}
                        >
                          {formatTime(message.created_at)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input area */}
              {selectedConversation.status === 'open' ? (
                <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="Type a reply..."
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#3A5E63] focus:border-transparent"
                      disabled={isSending}
                    />
                    <button
                      type="submit"
                      disabled={!inputValue.trim() || isSending}
                      className="bg-[#3A5E63] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#2d4a4f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Send
                    </button>
                  </div>
                </form>
              ) : (
                <div className="p-4 border-t border-gray-200 bg-gray-100 text-center text-sm text-gray-500">
                  This conversation is closed. Reopen to reply.
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-12 h-12 mx-auto mb-3 text-gray-300"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
                  />
                </svg>
                <p className="text-sm">Select a conversation to view messages</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
